import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Trophy, Calendar, Users, Search, Filter } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Tournaments() {
  const [tournaments, setTournaments] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    game: 'all',
    status: 'all',
    format: 'all',
    search: ''
  });

  useEffect(() => {
    fetchGames();
    fetchTournaments();
  }, []);

  const fetchGames = async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments/games/`, { withCredentials: true });
      setGames(data.games || []);
    } catch (e) {
      console.error('Failed to fetch games:', e);
    }
  };

  const fetchTournaments = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.game !== 'all') params.append('game', filters.game);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.format !== 'all') params.append('format', filters.format);
      
      const queryString = params.toString();
      const url = queryString ? `${API}/tournaments/?${queryString}` : `${API}/tournaments/`;
      const { data } = await axios.get(url, { withCredentials: true });
      setTournaments(data);
    } catch (e) {
      console.error('Failed to fetch tournaments:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTournaments();
  }, [filters.game, filters.status, filters.format]);

  const filteredTournaments = tournaments.filter(t => 
    t.name.toLowerCase().includes(filters.search.toLowerCase()) ||
    t.game.toLowerCase().includes(filters.search.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="tournaments-page">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-white mb-2">
            Tournaments
          </h1>
          <p className="text-[#A1A1AA]">
            Discover and join esports competitions across multiple games
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search tournaments..."
              className="pl-10 bg-[#121214] border-[#27272A] text-white placeholder:text-[#A1A1AA]"
              data-testid="tournament-search-input"
            />
          </div>
          
          <Select value={filters.game} onValueChange={(v) => setFilters({ ...filters, game: v })}>
            <SelectTrigger className="w-full sm:w-40 bg-[#121214] border-[#27272A] text-white" data-testid="game-filter">
              <SelectValue placeholder="All Games" />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-[#27272A]">
              <SelectItem value="all">All Games</SelectItem>
              {games.map(game => (
                <SelectItem key={game} value={game}>{game}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-full sm:w-40 bg-[#121214] border-[#27272A] text-white" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-[#27272A]">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="full">Full</SelectItem>
              <SelectItem value="live">Live</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.format} onValueChange={(v) => setFilters({ ...filters, format: v })}>
            <SelectTrigger className="w-full sm:w-32 bg-[#121214] border-[#27272A] text-white" data-testid="format-filter">
              <SelectValue placeholder="Format" />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-[#27272A]">
              <SelectItem value="all">All Formats</SelectItem>
              <SelectItem value="1v1">1v1</SelectItem>
              <SelectItem value="5v5">5v5</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tournament Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, idx) => (
              <div key={idx} className="h-64 rounded-md bg-[#121214] animate-pulse" />
            ))}
          </div>
        ) : filteredTournaments.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 mx-auto text-[#27272A] mb-4" />
            <h3 className="font-outfit font-bold text-xl text-white mb-2">No tournaments found</h3>
            <p className="text-[#A1A1AA]">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTournaments.map((tournament, idx) => (
              <div 
                key={tournament.id}
                className="tournament-card overflow-hidden animate-fade-in"
                style={{ animationDelay: `${idx * 0.05}s` }}
                data-testid={`tournament-card-${tournament.id}`}
              >
                {/* Image */}
                <div className="h-32 overflow-hidden">
                  <img 
                    src={tournament.image_url || 'https://images.unsplash.com/photo-1576990049702-8418081b420e?w=400&h=200&fit=crop'}
                    alt={tournament.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      tournament.status === 'full' ? 'badge-full' : 
                      tournament.status === 'live' ? 'badge-live' : 'badge-upcoming'
                    }`}>
                      {tournament.status}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#1A1A1E] text-[#A1A1AA]">
                      {tournament.format}
                    </span>
                  </div>
                  
                  <h3 className="font-outfit font-bold text-white mb-1 truncate">{tournament.name}</h3>
                  <p className="text-sm text-[#00F0FF] mb-3">{tournament.game}</p>
                  
                  <div className="flex items-center justify-between text-sm text-[#A1A1AA] mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {tournament.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {tournament.participants}/{tournament.max_participants}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#A1A1AA]">Prize Pool</p>
                      <p className="font-outfit font-bold text-[#00F0FF]">{tournament.prize_pool.toLocaleString()} pts</p>
                    </div>
                    <Button 
                      size="sm"
                      className={tournament.status === 'full' 
                        ? 'bg-[#27272A] text-[#A1A1AA] cursor-not-allowed' 
                        : 'bg-[#00F0FF] text-black hover:bg-[#33F3FF]'
                      }
                      disabled={tournament.status === 'full'}
                      data-testid={`join-tournament-${tournament.id}`}
                    >
                      {tournament.status === 'full' ? 'Full' : 'Join'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
